import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Eye, EyeOff, Mail, Lock, User } from "lucide-react";

type Mode = "login" | "register" | "forgot";

// Check if Supabase is configured
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
// Relaxed check so local stacks (127.0.0.1) are accepted too
const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

const syncProfileWhatsapp = async (
  userId: string,
  metadata?: Record<string, any>,
  fallbackWhatsapp?: string,
  fallbackName?: string,
) => {
  if (!isSupabaseConfigured) return;

  const phone = (metadata?.whatsapp ?? fallbackWhatsapp ?? '').toString().trim();
  const normalizedPhone = phone.length ? phone : null;

  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('id, whatsapp')
      .eq('user_id', userId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.warn('Não foi possível carregar o perfil para sincronizar o WhatsApp:', error.message || error);
      return;
    }

    if (!profile) {
      await supabase.from('profiles').upsert({
        user_id: userId,
        name: (metadata?.name ?? fallbackName ?? 'Usuário').toString(),
        avatar: '🔥',
        whatsapp: normalizedPhone,
        consent_at: metadata?.consent_at ?? new Date().toISOString(),
      }, { onConflict: 'user_id' });
      return;
    }

    if (normalizedPhone && profile.whatsapp !== normalizedPhone) {
      await supabase
        .from('profiles')
        .update({ whatsapp: normalizedPhone, updated_at: new Date().toISOString() })
        .eq('user_id', userId);
    }
  } catch (err) {
    console.warn('Não foi possível sincronizar o WhatsApp com o perfil:', err);
  }
};

const Login: React.FC = () => {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyEmailPrompt, setShowVerifyEmailPrompt] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Reset form when mode changes
    setPassword("");
    setName("");
    setWhatsapp("");
    setConfirmPassword("");
    setAcceptTerms(false);
  }, [mode]);

  const notifyDemoAuth = () => {
    // Persist demo auth flag so app reloads / remembers across tabs
    localStorage.setItem('demoAuth', 'true');
    // Notify the app (current window) to pick up demo session immediately
    window.dispatchEvent(new CustomEvent('demo-authenticated'));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // If supabase is not configured, show helpful message and simulate login (demo)
    if (!isSupabaseConfigured) {
      toast.success("Modo desenvolvimento: login simulado");
      notifyDemoAuth();
      setLoading(false);
      navigate("/", { replace: true });
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      console.log('signInWithPassword result:', { data, error });

      if (error) {
        toast.error(error.message || "Erro ao fazer login");
      } else {
        // Ensure session is available before navigating. Sometimes session propagation
        // may be slightly delayed; try to fetch the current session and wait shortly.
        let session = null;
        try {
          const sessionRes = await supabase.auth.getSession();
          session = sessionRes?.data?.session ?? null;
        } catch (e) {
          console.warn('Error getting session after signIn:', e);
        }

        // If no session yet, wait up to 2 seconds for auth state change
        if (!session) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => resolve(), 2000);
            const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
              if (event === 'SIGNED_IN') {
                clearTimeout(timeout);
                subscription.unsubscribe();
                resolve();
              }
            });
          });
        }

        // Active protected resource check: try to read the profiles row for this user
        // Retry a few times to handle small propagation delays. Only navigate when
        // the protected call succeeds to avoid the app seeing session=null and
        // redirecting back to /login.
        const maxAttempts = 4;
        let attempt = 0;
        let ok = false;
        const userId = data?.user?.id;
        while (attempt < maxAttempts && !ok) {
          attempt += 1;
          try {
            // Attempt a simple select on profiles (RLS will apply)
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('id')
              .eq('user_id', userId)
              .limit(1)
              .maybeSingle();

            if (!profileError) {
              ok = true;
              break;
            }
            console.warn(`Protected check attempt ${attempt} failed:`, profileError.message || profileError);
          } catch (err) {
            console.warn(`Protected check attempt ${attempt} unexpected error:`, err);
          }

          // wait before retrying
          await new Promise((r) => setTimeout(r, 500 * attempt));
        }

        if (!ok) {
          toast.error('Login efetuado, mas não foi possível validar a sessão com o servidor. Tente novamente.');
          console.error('Protected resource check failed after login for user', userId);
          setLoading(false);
          return;
        }

        if (userId) {
          await syncProfileWhatsapp(userId, data?.user?.user_metadata, undefined, data?.user?.user_metadata?.name);
        }

        toast.success("Login realizado com sucesso!");
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      console.error("Login error:", err);
      toast.error(err?.message || "Erro ao tentar fazer login");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Client-side validations for registration
    const trimmedName = name.trim();
    const hasSurname = trimmedName.split(/\s+/).length >= 2;
    if (!hasSurname) {
      toast.error("Por favor insira seu nome completo (nome e sobrenome).");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Senha e confirmação não coincidem.");
      setLoading(false);
      return;
    }

    if (!acceptTerms) {
      toast.error("Você precisa concordar com os termos e a política de privacidade.");
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured) {
      toast.success("Modo desenvolvimento: conta criada (simulada)");
      notifyDemoAuth();
      setLoading(false);
      navigate("/", { replace: true });
      return;
    }

    try {
      // signUp with user metadata (name, whatsapp)
      const consentAt = new Date().toISOString();

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            name: trimmedName,
            whatsapp: whatsapp.trim(),
            consent_at: consentAt,
          },
          // Redirect to callback after email confirmation
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });

      if (error) {
        toast.error(error.message || "Erro ao criar conta");
        console.error("Signup error:", error);
      } else {
        if (data?.user?.id && data?.session) {
          await syncProfileWhatsapp(data.user.id, data.user.user_metadata, whatsapp, trimmedName);
        }

        setPendingVerificationEmail(email.trim());
        setShowVerifyEmailPrompt(true);
        toast.success("✅ Conta criada! Verifique seu e-mail para confirmação.");
        setMode("login");
        setEmail("");
        setPassword("");
        setName("");
        setWhatsapp("");
        setConfirmPassword("");
        setAcceptTerms(false);
      }
    } catch (err: any) {
      console.error("Register error:", err);
      toast.error(err?.message || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!isSupabaseConfigured) {
      toast.success("Modo desenvolvimento: email de recuperação simulado");
      setLoading(false);
      setMode("login");
      return;
    }

    try {
      // ✅ CORRIGIR: Adicionar redirectTo
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) {
        toast.error(error.message || "Erro ao enviar email de recuperação");
        console.error("Reset password error:", error);
      } else {
        toast.success("✅ Email de recuperação enviado! Verifique sua caixa de entrada.");
        setMode("login");
        setEmail("");
      }
    } catch (err: any) {
      console.error("Forgot password error:", err);
      toast.error(err?.message || "Erro ao solicitar recuperação de senha");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#1a1f2e]">
      {showVerifyEmailPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6">
          <div className="bg-[#1e2538] border border-[#3d8a7d] rounded-lg p-6 max-w-sm w-full text-center shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Agora, verifique seu email</h3>
            <p className="text-gray-300 text-sm mb-4">
              Enviamos um link para {pendingVerificationEmail || "seu email"}. Clique em "Confirmar email" na mensagem para ativar sua conta.
            </p>
            <button
              type="button"
              onClick={() => setShowVerifyEmailPrompt(false)}
              className="w-full bg-[#3d8a7d] hover:bg-[#347a6d] text-white py-2.5 rounded-md font-medium transition-colors"
            >
              Ok, vou verificar
            </button>
          </div>
        </div>
      )}
      <div className="w-full max-w-md px-6">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-4 backdrop-blur-sm">
            <img 
              src="https://i.postimg.cc/SK6PNf6P/Assistente-financeiro-(150-x-150-px).png" 
              alt="Grana em Dia" 
              className="w-16 h-16 rounded-xl"
            />
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <img 
              src="https://i.postimg.cc/SK6PNf6P/Assistente-financeiro-(150-x-150-px).png" 
              alt="" 
              className="w-7 h-7 rounded-md"
            />
            Grana em <span className="text-[#3d8a7d]">Dia</span>
          </h1>
          <p className="text-gray-400 mt-1">Entre na sua conta</p>
          {!isSupabaseConfigured && (
            <div className="mt-2 px-3 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded-full">
              Modo desenvolvimento ativo
            </div>
          )}
        </div>

        {/* Form Container */}
        <div className="bg-[#1e2538] rounded-lg p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            {mode === "login" ? "Entrar" : mode === "register" ? "Criar conta" : "Recuperar senha"}
          </h2>

          <form onSubmit={mode === "login" ? handleLogin : mode === "register" ? handleRegister : handleForgot}>
            {/* Name Field - Only for register */}
            {mode === "register" && (
              <div className="mb-4">
                <label htmlFor="name" className="block text-gray-400 mb-2">
                  Nome
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-[#f5f5c6] text-gray-800 rounded-md py-3 px-10 focus:outline-none"
                    placeholder="Seu nome completo"
                    required
                  />
                </div>
              </div>
            )}

            {/* WhatsApp - only for register */}
            {mode === "register" && (
              <div className="mb-4">
                <label htmlFor="whatsapp" className="block text-gray-400 mb-2">
                  WhatsApp
                </label>
                <div className="relative">
                  <input
                    id="whatsapp"
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="w-full bg-[#f5f5c6] text-gray-800 rounded-md py-3 px-3 focus:outline-none"
                    placeholder="(DD) 9XXXX-XXXX"
                    required
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Usaremos o WhatsApp para verificação/segurança de acesso às suas informações.</p>
              </div>
            )}

            {/* Email Field */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-gray-400 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#f5f5c6] text-gray-800 rounded-md py-3 px-10 focus:outline-none"
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            {/* Password Field - Only show for login and register */}
            {mode !== "forgot" && (
              <div className="mb-6">
                <label htmlFor="password" className="block text-gray-400 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#f5f5c6] text-gray-800 rounded-md py-3 px-10 pr-10 focus:outline-none"
                    placeholder="••••••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {/* Confirm password - only for register */}
            {mode === "register" && (
              <div className="mb-4">
                <label htmlFor="confirmPassword" className="block text-gray-400 mb-2">Confirme a senha</label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[#f5f5c6] text-gray-800 rounded-md py-3 px-3 focus:outline-none"
                    placeholder="Repita a senha"
                    required
                  />
                </div>
              </div>
            )}

            {/* Terms checkbox - only for register */}
            {mode === "register" && (
              <div className="mb-4 flex items-start">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 mr-2"
                />
                <label htmlFor="acceptTerms" className="text-gray-400 text-sm">
                  Eu li e concordo com os <a className="text-[#3d8a7d] underline" href="https://infinityai.com.br/termos-de-uso-grana-em-dia/" target="_blank" rel="noreferrer">Termos de Uso</a> e a <a className="text-[#3d8a7d] underline" href="https://infinityai.com.br/politicas-de-privacidade/" target="_blank" rel="noreferrer">Política de Privacidade</a>. Autorizo o uso dos dados conforme a legislação (LGPD).
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#3d8a7d] hover:bg-[#347a6d] text-white py-3 rounded-md font-medium transition-colors"
            >
              {loading
                ? "Processando..."
                : mode === "login"
                ? "Entrar"
                : mode === "register"
                ? "Criar conta"
                : "Enviar link"}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center">
            {mode === "login" && (
              <>
                <button
                  onClick={() => setMode("forgot")}
                  className="text-[#3d8a7d] hover:text-[#4db8a8] text-sm transition-colors"
                >
                  Esqueceu sua senha?
                </button>
                <div className="mt-3">
                  <span className="text-gray-400 text-sm">Não tem conta? </span>
                  <button
                    onClick={() => setMode("register")}
                    className="text-[#3d8a7d] hover:text-[#4db8a8] text-sm transition-colors"
                  >
                    Criar uma
                  </button>
                </div>
              </>
            )}

            {mode === "register" && (
              <div>
                <span className="text-gray-400 text-sm">Já tem uma conta? </span>
                <button
                  onClick={() => setMode("login")}
                  className="text-[#3d8a7d] hover:text-[#4db8a8] text-sm transition-colors"
                >
                  Entrar
                </button>
              </div>
            )}

            {mode === "forgot" && (
              <div>
                <span className="text-gray-400 text-sm">Lembrou sua senha? </span>
                <button
                  onClick={() => setMode("login")}
                  className="text-[#3d8a7d] hover:text-[#4db8a8] text-sm transition-colors"
                >
                  Voltar ao login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;